import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon, type IconName } from './Icon';

/**
 * تأكيد الإجراء.
 *
 * الإجراءات السريعة تُغلق نافذتها عند النجاح وتعود إلى الجدول، فلا يبقى ما
 * يقول إن شيئًا وقع. والمستخدم الذي لا يرى أثرًا يضغط ثانيةً — فيُنفَّذ
 * الإجراء مرتين، أو يظنّه فشل ويتركه.
 *
 * والرسالة تختفي وحدها: تأكيدٌ يحتاج إغلاقًا يدويًا يصير عملًا إضافيًا بعد
 * كل عمل.
 *
 * وليست بديلًا عن عرض الخطأ: الإخفاق يبقى في مكانه داخل النافذة حيث يُقرأ
 * ويُعاد المحاولة. هذه للنجاح وحده — والنجاح لا يحتاج أكثر من سطر يمرّ.
 */

export type ToastKind = 'success' | 'info';

interface Toast {
  id: number;
  text: string;
  kind: ToastKind;
}

const ToastContext = createContext<(text: string, kind?: ToastKind) => void>(() => {});

/** يُطلق رسالة تأكيد. آمن خارج المزوّد: لا يفعل شيئًا. */
export function useToast(): (text: string, kind?: ToastKind) => void {
  return useContext(ToastContext);
}

const LIFETIME_MS = 3200;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const push = useCallback((text: string, kind: ToastKind = 'success') => {
    const id = nextId.current++;
    setItems((cur) => {
      /*
       * ثلاث رسائل على الأكثر.
       *
       * إجراء جماعي قد يُطلق عشرين، فتغطّي الشاشة وتُقرأ آخرها وحدها.
       * والأقدم يخرج لأن الأحدث هو ما يخصّ ما فعله المستخدم للتوّ.
       */
      const kept = cur.slice(-2);
      return [...kept, { id, text, kind }];
    });
  }, []);

  /*
   * مؤقّت واحد يمسح المنتهية، لا مؤقّت لكل رسالة.
   *
   * مؤقّت لكل رسالة يعني تنظيفًا لكل واحد، ورسالةً تبقى معلّقة إن أُزيل
   * المكوّن قبل انطلاقه.
   */
  useEffect(() => {
    if (!items.length) return;
    const timer = window.setTimeout(() => {
      setItems((cur) => cur.slice(1));
    }, LIFETIME_MS);
    return () => window.clearTimeout(timer);
  }, [items]);

  const value = useMemo(() => push, [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {items.length > 0 &&
        createPortal(
          <div className="toasts" role="status" aria-live="polite">
            {items.map((t) => (
              <div key={t.id} className={`toast toast-${t.kind} pulse-ok`}>
                <Icon name={ICONS[t.kind]} size={16} />
                <span>{t.text}</span>
              </div>
            ))}
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
}

const ICONS: Record<ToastKind, IconName> = {
  success: 'check',
  info: 'zap',
};
